import axios from 'axios';
import { createBrowserHistory } from 'history';
import { addCsrfTokenToHeaders, configureCsrfProtection, fetchCsrfToken } from './csrfProtection';
import { sanitizeAxiosRequestConfig } from './sanitizeRequestData';

const history = createBrowserHistory();

// Create an Axios instance with default settings
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, 
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-store',
  },
  withCredentials: true, // Important for cookies
});

// Configure CSRF protection
configureCsrfProtection(axiosInstance);

// Fetch initial CSRF token
fetchCsrfToken().catch(error => {
  console.error('Failed to fetch initial CSRF token:', error);
});

// Add CSRF headers and Authorization token to all requests
axiosInstance.interceptors.request.use(
  async (config) => {
    // Sanitize outgoing request bodies before auth/CSRF headers are applied.
    sanitizeAxiosRequestConfig(config);

    // Add JWT token from localStorage if it exists
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Ensure we have a CSRF token before making the request
    if (!config.headers['X-CSRF-Token']) {
      try {
        await fetchCsrfToken();
      } catch (error) {
        console.error('Failed to fetch CSRF token:', error);
      }
    }
    config.headers = addCsrfTokenToHeaders(config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle CSRF token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403 && error.response?.data?.message === 'CSRF token invalid') {
      // Token expired or invalid, try to refresh it
      try {
        await fetchCsrfToken();
        // Retry the original request
        const config = error.config;
        config.headers = addCsrfTokenToHeaders(config.headers);
        return axiosInstance(config);
      } catch (refreshError) {
        console.error('Failed to refresh CSRF token:', refreshError);
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export { history };
export default axiosInstance;
